/**
 * Signup funnel — FFC product order:
 *   signups → activate → paid  (conversion; creates logos/MRR)
 *   paid → bank-link           (engagement; Basic+ / Plaid — not a paid gate)
 *
 * Legacy packs used activate→bank-link→paid (inverted). normalizeFunnel() migrates.
 */

function roundMoney(n) {
  return Math.round(n * 100) / 100;
}

export const FORECAST_PLAN_KEYS = ['basic', 'premium', 'enterprise'];

/**
 * Normalize the paid-plan weights used by the forecast.
 * Plan mix is treated as relative weights so a scenario with 0.8 total still
 * allocates all paid logos rather than silently leaving 20% unclassified.
 * @param {Record<string, number>} planMix
 * @returns {Record<string, number>}
 */
export function normalizePlanMix(planMix) {
  const raw = Object.fromEntries(
    FORECAST_PLAN_KEYS.map((plan) => [plan, Math.max(0, Number(planMix?.[plan]) || 0)])
  );
  const total = Object.values(raw).reduce((sum, share) => sum + share, 0);
  if (total <= 0) return raw;
  return Object.fromEntries(
    Object.entries(raw).map(([plan, share]) => [plan, share / total])
  );
}

/**
 * Allocate aggregate paid logos across the configured paid-plan mix.
 * @param {number} logos
 * @param {Record<string, number>} planMix
 * @returns {Record<string, number>}
 */
export function splitLogosByPlan(logos, planMix) {
  const totalLogos = Math.max(0, Number(logos) || 0);
  const normalized = normalizePlanMix(planMix);
  return Object.fromEntries(
    FORECAST_PLAN_KEYS.map((plan) => [plan, roundPct(totalLogos * normalized[plan])])
  );
}

/**
 * Opening mix stays frozen unless these upgrade rates run after month 0.
 * Enterprise can appear from Premium upgrades even when opening share is 0.
 * It is never invented from a 0→0 share multiply.
 */
export const DEFAULT_PLAN_MIX_GROWTH = {
  upgradeBasicToPremiumPctMo: 0.5,
  upgradePremiumToEnterprisePctMo: 0,
};

function clampUpgradePct(value, fallback = 0) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(100, Math.max(0, n));
}

/**
 * @param {object} [growth]
 * @returns {{ upgradeBasicToPremiumPctMo: number, upgradePremiumToEnterprisePctMo: number }}
 */
export function normalizePlanMixGrowth(growth = {}) {
  const source = growth && typeof growth === 'object' ? growth : {};
  return {
    upgradeBasicToPremiumPctMo: clampUpgradePct(
      source.upgradeBasicToPremiumPctMo,
      DEFAULT_PLAN_MIX_GROWTH.upgradeBasicToPremiumPctMo
    ),
    upgradePremiumToEnterprisePctMo: clampUpgradePct(
      source.upgradePremiumToEnterprisePctMo,
      DEFAULT_PLAN_MIX_GROWTH.upgradePremiumToEnterprisePctMo
    ),
  };
}

/**
 * One month of Basic→Premium and Premium→Enterprise upgrades.
 * Shares stay normalized. A 0 Enterprise share stays 0 unless Premium upgrades into it.
 * @param {Record<string, number>} planMix
 * @param {object} [growth]
 */
export function evolvePlanMix(planMix, growth = {}) {
  const mix = normalizePlanMix(planMix);
  const fromBasic =
    mix.basic * (clampUpgradePct(growth?.upgradeBasicToPremiumPctMo, 0) / 100);
  const fromPremium =
    mix.premium * (clampUpgradePct(growth?.upgradePremiumToEnterprisePctMo, 0) / 100);
  return normalizePlanMix({
    basic: mix.basic - fromBasic,
    premium: mix.premium - fromPremium + fromBasic,
    enterprise: mix.enterprise + fromPremium,
  });
}

/**
 * Mix after `monthIndex` upgrade steps. Month 0 is the opening mix.
 * @param {Record<string, number>} openingMix
 * @param {object} [growth]
 * @param {number} monthIndex
 */
export function planMixAtMonth(openingMix, growth, monthIndex) {
  let mix = normalizePlanMix(openingMix);
  const steps = Math.max(0, Math.floor(Number(monthIndex) || 0));
  for (let i = 0; i < steps; i += 1) {
    mix = evolvePlanMix(mix, growth);
  }
  return mix;
}

/**
 * MRR lift from mix upgrades on retained logos (before new logos join).
 * @param {number} retainedLogos
 * @param {Record<string, number>} fromMix
 * @param {Record<string, number>} toMix
 * @param {Record<string, number>} arpuByTier
 */
export function upgradeExpansionFromMix(retainedLogos, fromMix, toMix, arpuByTier) {
  const logos = Math.max(0, Number(retainedLogos) || 0);
  const delta = weightedArpu(arpuByTier, toMix) - weightedArpu(arpuByTier, fromMix);
  return roundMoney(logos * delta);
}

function clampPct(value, fallback = 0) {
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.min(100, Math.max(0, n));
}

/**
 * Normalize funnel keys; collapse legacy bank-link-before-paid into activate→paid.
 * @param {object} funnel
 */
export function normalizeFunnel(funnel = {}) {
  const hasNew = funnel.activateToPaidPct != null;
  let activateToPaidPct = funnel.activateToPaidPct;
  let paidToBankLinkPct = funnel.paidToBankLinkPct;

  if (!hasNew && (funnel.activateToBankLinkPct != null || funnel.bankLinkToPaidPct != null)) {
    // Preserve cumulative: old B% × C% → new activate→paid %
    const b = (funnel.activateToBankLinkPct ?? 0) / 100;
    const c = (funnel.bankLinkToPaidPct ?? 0) / 100;
    activateToPaidPct = Math.round(b * c * 1000) / 10;
    // The legacy model did not contain a paid → bank-link rate. Do not reuse
    // bankLink → paid: it is the inverse conditional and would misstate
    // linked-logo volume. Use the new-model default instead.
    paidToBankLinkPct = funnel.paidToBankLinkPct ?? 50;
  }

  const lagValue = Number(funnel.conversionLagMonths ?? 2);
  const signupsValue = Number(funnel.baseMonthlySignups ?? 10);
  const signupGrowthValue = Number(funnel.signupGrowthPctMo ?? 5);

  return {
    signupToActivatePct: clampPct(funnel.signupToActivatePct ?? 60, 60),
    activateToPaidPct: clampPct(activateToPaidPct ?? 10, 10),
    paidToBankLinkPct: clampPct(paidToBankLinkPct ?? 50, 50),
    conversionLagMonths: Math.min(
      12,
      Math.max(0, Math.floor(Number.isFinite(lagValue) ? lagValue : 2))
    ),
    baseMonthlySignups: Math.max(0, Number.isFinite(signupsValue) ? signupsValue : 10),
    signupGrowthPctMo: Math.max(
      -100,
      Math.min(1000, Number.isFinite(signupGrowthValue) ? signupGrowthValue : 5)
    ),
  };
}

/**
 * Cumulative signup → paid conversion (product of conversion stages only).
 * Bank-link rate is post-paid and excluded.
 * @param {object} funnel
 */
export function cumulativeSignupToPaid(funnel) {
  const n = normalizeFunnel(funnel);
  const a = (n.signupToActivatePct ?? 0) / 100;
  const b = (n.activateToPaidPct ?? 0) / 100;
  return a * b;
}

/**
 * Apply conversion delta from events (percentage points on activate→paid).
 * @param {object} funnel
 * @param {number} deltaPp
 */
export function adjustFunnelConversion(funnel, deltaPp = 0) {
  const n = normalizeFunnel(funnel);
  return {
    ...n,
    activateToPaidPct: Math.min(100, Math.max(0, n.activateToPaidPct + (Number(deltaPp) || 0))),
  };
}

/**
 * Push signups into pipeline and extract matured paid conversions.
 * @param {number} signupsThisMonth
 * @param {object} funnel
 * @param {number} monthIndex
 * @param {Map<number, number>} signupPipeline — monthIndex → pending signups
 * @param {number} [conversionDeltaPp]
 */
export function processFunnelMonth(
  signupsThisMonth,
  funnel,
  monthIndex,
  signupPipeline,
  conversionDeltaPp = 0
) {
  const adjusted = adjustFunnelConversion(funnel, conversionDeltaPp);
  const lag = Math.max(0, adjusted.conversionLagMonths ?? 2);
  const convRate = cumulativeSignupToPaid(adjusted);

  signupPipeline.set(monthIndex, (signupPipeline.get(monthIndex) || 0) + signupsThisMonth);

  const matureMonth = monthIndex - lag;
  const maturedSignups = matureMonth >= 0 ? signupPipeline.get(matureMonth) || 0 : 0;
  if (matureMonth >= 0) signupPipeline.delete(matureMonth);

  const newPaidLogos = maturedSignups * convRate;

  return {
    newPaidLogos: roundPct(newPaidLogos),
    convRate,
    signupsThisMonth,
    maturedSignups,
  };
}

function roundPct(n) {
  return Math.round(n * 1000) / 1000;
}

/**
 * Weighted ARPU from tier mix.
 * @param {Record<string, number>} arpuByTier
 * @param {Record<string, number>} planMix — shares summing ~1
 */
export function weightedArpu(arpuByTier, planMix) {
  let total = 0;
  let weight = 0;
  for (const [tier, share] of Object.entries(planMix || {})) {
    const arpu = Math.max(0, Number(arpuByTier?.[tier]) || 0);
    const safeShare = Math.max(0, Number(share) || 0);
    total += arpu * safeShare;
    weight += safeShare;
  }
  // An explicit zero mix represents no monetized tier; do not silently turn
  // a Free-only Economics sync into Basic revenue.
  if (weight <= 0) return 0;
  return roundMoney(total / weight);
}

/**
 * New MRR from new paid logos.
 * @param {number} newPaidLogos
 * @param {number} arpu
 */
export function newMrrFromLogos(newPaidLogos, arpu) {
  return roundMoney(newPaidLogos * arpu);
}

/**
 * Bank-linking is a post-paid engagement step. It cannot create a paying
 * logo, but it does determine how many paying logos are expected to use Plaid.
 */
export function bankLinkedLogosFromPaid(paidLogos, funnel) {
  const rate = normalizeFunnel(funnel).paidToBankLinkPct / 100;
  return Math.max(0, Number(paidLogos) || 0) * rate;
}
